import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { QrCode, Edit3, Home, Plus, CheckCircle } from 'lucide-react'
import { buildApiUrl, buildQrUrl } from '../config'
import { Card, Button, Alert } from './ui'
import { timeAgo } from '../utils/timeAgo'
import { linkify } from '../utils/linkify'

interface Message {
  handle: string
  message: string
  imageUrl?: string
  updatedAt: string
  viewCount?: number
}

const SkeletonLoader = () => (
  <div className="min-h-[60vh] flex items-center justify-center p-4">
    <div className="w-full max-w-lg mx-auto animate-pulse">
      <div className="rounded-2xl border border-slate-200/70 bg-white dark:bg-slate-900 dark:border-slate-800 p-8 shadow-soft">
        <div className="flex justify-center mb-6">
          <div className="h-7 w-28 bg-slate-200 dark:bg-slate-700 rounded-full" />
        </div>
        <div className="space-y-3 mb-8">
          <div className="h-8 bg-slate-200 dark:bg-slate-700 rounded-xl w-3/4 mx-auto" />
          <div className="h-8 bg-slate-200 dark:bg-slate-700 rounded-xl w-1/2 mx-auto" />
        </div>
        <div className="h-10 bg-slate-100 dark:bg-slate-800 rounded-xl w-40 mx-auto" />
      </div>
    </div>
  </div>
)

const MessageView = () => {
  const { handle } = useParams<{ handle: string }>()
  const navigate = useNavigate()
  const [message, setMessage] = useState<Message | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!handle) return

    const fetchMessage = async () => {
      try {
        setLoading(true)
        const response = await fetch(buildApiUrl(`/messages/${handle}`))

        if (response.status === 404) {
          setError('not_found')
          return
        }

        if (!response.ok) {
          throw new Error('Failed to fetch message')
        }

        const data = await response.json()
        setMessage(data)
        recordScan(handle)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred')
      } finally {
        setLoading(false)
      }
    }

    fetchMessage()
  }, [handle])

  const recordScan = async (handle: string) => {
    const sessionKey = `yowo:scan:${handle}`
    if (sessionStorage.getItem(sessionKey)) return
    sessionStorage.setItem(sessionKey, 'pending')
    try {
      const response = await fetch(buildApiUrl(`/messages/${handle}/scan`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      })
      if (!response.ok) throw new Error('Scan recording failed')
      const data = await response.json()
      sessionStorage.setItem(sessionKey, 'recorded')
      if (typeof data.viewCount === 'number') {
        setMessage(current => current ? { ...current, viewCount: data.viewCount } : current)
      }
    } catch {
      sessionStorage.removeItem(sessionKey)
    }
  }

  if (loading) return <SkeletonLoader />

  if (error === 'not_found') {
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-4">
        <div className="w-full max-w-md mx-auto text-center animate-fade-in">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-brand/10 to-purple-100 dark:from-brand/20 dark:to-purple-900/30 mb-6">
            <QrCode size={36} className="text-brand" />
          </div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100 mb-3">
            Huh. Nothing here.
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mb-8 text-lg">
            This code is a mystery wrapped in a mystery. It hasn't been set up yet.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button variant="primary" size="lg" onClick={() => navigate('/')}>
              <Home size={16} className="mr-2" />
              Go Home
            </Button>
            <Button variant="secondary" size="lg" onClick={() => navigate('/setup')}>
              <Plus size={16} className="mr-2" />
              Create a Code
            </Button>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-4">
        <div className="w-full max-w-md mx-auto text-center animate-fade-in">
          <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100 mb-3">This code is temporarily unavailable</h1>
          <p className="text-slate-500 dark:text-slate-400 mb-8">Your code still exists. Check your connection and try again.</p>
          <Button variant="primary" size="lg" onClick={() => window.location.reload()}>
            Try Again
          </Button>
        </div>
      </div>
    )
  }

  if (!message) return null

  const hasEditToken = (() => {
    const token = localStorage.getItem(`qr:${message.handle}`)
    return token && token.trim() !== ''
  })()

  return (
    <>
      <Helmet>
        <title>@{message.handle} on YouWont</title>
        <meta name="description" content={message.message} />
        <meta property="og:title" content={`@${message.handle} - YouWont`} />
        <meta property="og:description" content={message.message} />
        <meta property="og:url" content={buildQrUrl(message.handle)} />
      </Helmet>

      <div className="min-h-[60vh] flex items-center justify-center p-4 py-12">
        <div className="w-full max-w-lg mx-auto animate-fade-in">
          {/* Main Message Card */}
          <Card className="text-center !p-8 sm:!p-10">
            {/* Handle Badge */}
            <div className="inline-flex items-center gap-1.5 px-4 py-1.5 bg-brand/10 dark:bg-brand/20 rounded-full mb-8">
              <span className="w-1.5 h-1.5 rounded-full bg-brand animate-pulse" />
              <span className="text-sm font-semibold text-brand dark:text-blue-400">
                youwont.xyz/u/{message.handle}
              </span>
            </div>

            {/* Image */}
            {message.imageUrl && (
              <div className="mb-8">
                <img
                  src={message.imageUrl}
                  alt="Message image"
                  className="w-full max-w-sm mx-auto h-auto rounded-2xl shadow-lift object-cover"
                  style={{ maxHeight: '300px' }}
                  onError={(e) => { e.currentTarget.style.display = 'none' }}
                />
              </div>
            )}

            {/* Message Text */}
            <div className="mb-8">
              <p className="text-3xl sm:text-4xl font-bold text-slate-900 dark:text-slate-100 whitespace-pre-wrap break-words leading-snug">
                {linkify(message.message)}
              </p>
            </div>

            {/* Owner Section */}
            {hasEditToken && (
              <Alert variant="success" className="text-left">
                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                  <div className="flex items-center gap-2 flex-1">
                    <CheckCircle size={16} className="shrink-0" />
                    <span>You own this QR code</span>
                  </div>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => navigate(`/dashboard/${message.handle}`)}
                    className="shrink-0"
                  >
                    <Edit3 size={14} className="mr-1.5" />
                    Manage
                  </Button>
                </div>
              </Alert>
            )}
          </Card>

          {/* Footer */}
          <div className="mt-6 text-center space-y-2">
            <p className="text-xs text-slate-400 dark:text-slate-500">
              Updated {timeAgo(message.updatedAt)}
            </p>
            {message.viewCount != null && message.viewCount > 0 && (
              <p className="text-xs text-slate-400 dark:text-slate-500">
                Scanned {message.viewCount.toLocaleString()} {message.viewCount === 1 ? 'time' : 'times'}
              </p>
            )}
            <div className="flex justify-center gap-2 pt-1">
              <Button variant="ghost" size="sm" onClick={() => navigate('/')}>
                <Home size={14} className="mr-1.5" />
                Home
              </Button>
            </div>
            <p className="text-xs text-slate-400 dark:text-slate-500 pt-1">
              Feeling inspired?{' '}
              <button
                onClick={() => navigate('/setup')}
                className="text-brand hover:text-brand-600 dark:text-blue-400 underline font-medium"
              >
                Make your own →
              </button>
            </p>
          </div>
        </div>
      </div>
    </>
  )
}

export default MessageView
